// ---------------------------------------------------------------------------
// Google Translate (Website Translator) integration.
//
// Loads the free Google Translate widget script exactly once, keeps its
// default UI hidden, and drives it through the `googtrans` cookie. The
// chosen language is mirrored in localStorage (same persistence pattern as
// the auth token in AuthContext) so it survives cookie clears and revisits.
//
// How it works:
//   1. initGoogleTranslate() is called once on app mount (LanguageSwitcher).
//   2. It restores any saved language into the `googtrans` cookie BEFORE the
//      Google script loads, so the page is translated immediately on load.
//   3. setLanguage(code) writes the cookie and reloads the page once. After
//      that, the widget's own MutationObserver translates everything React
//      renders (route changes, modals, toasts) with no further reloads.
//
// To remove this feature entirely: delete this file and
// components/common/LanguageSwitcher.js, remove the <LanguageSwitcher />
// line from App.js, and delete the "Google Translate" block in index.css.
// ---------------------------------------------------------------------------

const SCRIPT_ID = 'google-translate-script';
const CONTAINER_ID = 'google_translate_element';
const COOKIE_NAME = 'googtrans';
const STORAGE_KEY = 'preferredLanguage';

// The page is authored in English; 'en' acts as the "show original" option.
export const PAGE_LANGUAGE = 'en';

// The only languages exposed by the switcher.
export const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'id', label: 'Bahasa Indonesia' },
  { code: 'vi', label: 'Vietnamese' },
];

const INCLUDED_LANGUAGES = LANGUAGES
  .filter((l) => l.code !== PAGE_LANGUAGE)
  .map((l) => l.code)
  .join(',');

let initialized = false;

const isKnownLanguage = (code) => LANGUAGES.some((l) => l.code === code);

// --- googtrans cookie helpers ----------------------------------------------
// The widget reads translation state from a `googtrans` cookie shaped like
// "/en/id". On real domains it sometimes reads it from the parent domain,
// so the cookie is written/cleared on both the host and ".root-domain".

const cookieDomains = () => {
  const host = window.location.hostname;
  const domains = [''];
  if (host.includes('.') && !/^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    domains.push(`.${host.split('.').slice(-2).join('.')}`);
  }
  return domains;
};

const writeTranslateCookie = (langCode) => {
  cookieDomains().forEach((domain) => {
    document.cookie =
      `${COOKIE_NAME}=/${PAGE_LANGUAGE}/${langCode};path=/;max-age=31536000` +
      (domain ? `;domain=${domain}` : '');
  });
};

const clearTranslateCookie = () => {
  cookieDomains().forEach((domain) => {
    document.cookie =
      `${COOKIE_NAME}=;path=/;max-age=0` + (domain ? `;domain=${domain}` : '');
  });
};

const readTranslateCookie = () => {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]+)`));
  if (!match) return null;
  const parts = decodeURIComponent(match[1]).split('/'); // "/en/id" -> ["", "en", "id"]
  const code = parts[parts.length - 1];
  return isKnownLanguage(code) ? code : null;
};

// --- public API -------------------------------------------------------------

// Currently active language: the cookie is the source of truth for what is
// actually translated; localStorage is the fallback for the saved preference.
export const getSavedLanguage = () => {
  const fromCookie = readTranslateCookie();
  if (fromCookie) return fromCookie;
  const stored = localStorage.getItem(STORAGE_KEY);
  return isKnownLanguage(stored) ? stored : PAGE_LANGUAGE;
};

// Persist the choice and reload once so the widget applies it to the whole
// page from a clean state. Selecting English clears the cookie, which makes
// the widget show the original untranslated content.
export const setLanguage = (langCode) => {
  if (!isKnownLanguage(langCode)) return;
  localStorage.setItem(STORAGE_KEY, langCode);
  if (langCode === PAGE_LANGUAGE) {
    clearTranslateCookie();
  } else {
    writeTranslateCookie(langCode);
  }
  window.location.reload();
};

export const initGoogleTranslate = () => {
  if (initialized || document.getElementById(SCRIPT_ID)) return;
  initialized = true;

  patchDomMethodsForTranslate();

  // Re-apply a saved preference whose cookie was cleared (e.g. by the
  // browser) so the saved language is restored before the widget boots.
  const stored = localStorage.getItem(STORAGE_KEY);
  if (isKnownLanguage(stored) && stored !== PAGE_LANGUAGE && !readTranslateCookie()) {
    writeTranslateCookie(stored);
  }

  // Hidden host element for the widget, kept outside the React root so
  // React reconciliation never touches Google's DOM.
  if (!document.getElementById(CONTAINER_ID)) {
    const container = document.createElement('div');
    container.id = CONTAINER_ID;
    container.style.display = 'none';
    document.body.appendChild(container);
  }

  window.googleTranslateElementInit = () => {
    /* global google */
    new window.google.translate.TranslateElement(
      {
        pageLanguage: PAGE_LANGUAGE,
        includedLanguages: INCLUDED_LANGUAGES,
        autoDisplay: false,
      },
      CONTAINER_ID
    );
  };

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
  script.async = true;
  document.body.appendChild(script);
};

// --- React compatibility guard ----------------------------------------------
// Google Translate replaces text nodes with <font> wrappers. When React later
// removes or reorders those nodes it can throw "NotFoundError: Failed to
// execute 'removeChild' on 'Node'" and white-screen the app (see
// facebook/react#11538). These tolerant wrappers turn that hard crash into a
// no-op; they change nothing when translation is inactive.
const patchDomMethodsForTranslate = () => {
  if (window.__googleTranslateDomPatched) return;
  window.__googleTranslateDomPatched = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child && child.parentNode !== this) {
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      return newNode;
    }
    return originalInsertBefore.apply(this, arguments);
  };
};
